import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Card } from 'antd'
import { RightOutlined } from '@ant-design/icons'

import { bangkokClock, formatDateTime, formatDay } from './journeyTime'
import { relativeDay, wordsFor } from './journeyWords'
import { dayTotals, foldDays, foldedSteps, groupByDay, isOtherOrder, isVisit } from './journeyDays'
import { creditedMessage } from './journeyFacts'
import { StepRow } from './StepRow'
import { VisitRow } from './VisitRow'
import { AfterOrderPart } from './AfterOrderPart'
import { DayHeaderLine } from './DayHeaderLine'
import { HintLabel } from './HintLabel'
import {
  Clock,
  FoldedDay,
  FoldedTotals,
  GapLine,
  Muted,
  OrderDot,
  OrderRowGrid,
  OtherOrderLine,
  Panel
} from './journeyStyles'

const DEFAULT_WINDOW_DAYS = 30

const EmptySteps = ({ days, previousActivityAt, fromLineChat, lang }) => {
  const { t } = useTranslation('inquiries')
  return (
    <Panel>
      <div>{t('orderJourney.empty', { days })}</div>
      <Muted>{t(fromLineChat ? 'orderJourney.emptyLineHint' : 'orderJourney.emptyHint', { days })}</Muted>
      {previousActivityAt && (
        <div>
          <Muted>{t('orderJourney.previousActivity', { date: formatDateTime(previousActivityAt, lang) })}</Muted>
        </div>
      )}
    </Panel>
  )
}

const OtherOrderStep = ({ step, journey, lang }) => (
  <OtherOrderLine>
    {[wordsFor(journey, step.name, lang), ...(step.details || [])].join(' ')} · {formatDay(step.first_at, lang)}{' '}
    {bangkokClock(step.first_at)}
  </OtherOrderLine>
)

const FoldedDayLine = ({ day, journey, lang, onOpen }) => {
  const { t } = useTranslation('inquiries')
  const totals = dayTotals(day.steps)
    .map((total) => {
      const words = wordsFor(journey, total.name, lang)
      const label = total.kind === 'message' ? `${t('orderJourney.weSent')}: ${words}` : words
      return `${label} ${total.count}`
    })
    .join(', ')
  return (
    <FoldedDay type='button' aria-expanded='false' onClick={onOpen}>
      <RightOutlined style={{ fontSize: 12, color: 'var(--journey-muted)' }} />
      <span>{formatDay(day.firstAt, lang)}</span>
      <Muted>{relativeDay(t, day.daysBefore)}</Muted>
      <FoldedTotals>{totals}</FoldedTotals>
    </FoldedDay>
  )
}

const OrderRow = ({ journey }) => {
  const { t } = useTranslation('inquiries')
  return (
    <OrderRowGrid>
      <Clock>{bangkokClock(journey.order?.started_at)}</Clock>
      <div>
        <OrderDot />
        {t('orderJourney.orderRow', { number: journey.order?.order_number || '' })}
      </div>
    </OrderRowGrid>
  )
}

const DayBlock = ({ day, journey, lang, orderIds, credited, onOpen }) => {
  const { t } = useTranslation('inquiries')
  const otherOrders = day.steps.filter(isOtherOrder)
  const hasOwnSteps = day.steps.length > otherOrders.length
  const gap = day.gapBefore > 0 && <GapLine>· {t('orderJourney.gap', { count: day.gapBefore })} ·</GapLine>

  if (!day.open) {
    return (
      <>
        {gap}
        {hasOwnSteps && <FoldedDayLine day={day} journey={journey} lang={lang} onOpen={onOpen} />}
        {otherOrders.map((step, index) => (
          <OtherOrderStep key={`${step.name}-${index}`} step={step} journey={journey} lang={lang} />
        ))}
      </>
    )
  }

  return (
    <>
      {gap}
      {(hasOwnSteps || day.daysBefore === 0) && (
        <DayHeaderLine at={day.firstAt} daysBefore={day.daysBefore} lang={lang} />
      )}
      {day.steps.map((step, index) =>
        isOtherOrder(step) ? (
          <OtherOrderStep key={`${step.name}-${index}`} step={step} journey={journey} lang={lang} />
        ) : isVisit(step) ? (
          <VisitRow key={`visit-${step.first_at}`} visit={step} journey={journey} lang={lang} orderIds={orderIds} />
        ) : (
          <StepRow
            key={`${step.name}-${step.first_at}-${index}`}
            step={step}
            journey={journey}
            lang={lang}
            orderIds={orderIds}
            credited={step === credited}
          />
        )
      )}
    </>
  )
}

const JourneySteps = ({ journey, orderIds, lang }) => {
  const { t } = useTranslation('inquiries')
  const [allOpen, setAllOpen] = useState(false)
  const [openedDays, setOpenedDays] = useState([])
  const steps = journey.steps || []
  const after = journey.after_order
  const journeyWindow = journey.window || {}
  const windowDays = journeyWindow.days ?? DEFAULT_WINDOW_DAYS
  const { foldable, days } = foldDays(groupByDay(steps, journey.order?.started_at), { allOpen, openedDays })
  const credited = creditedMessage(steps, journey.attribution, journey.order?.started_at)
  const folded = foldedSteps(days)
  const toggleAll = () => {
    setAllOpen(!allOpen)
    setOpenedDays([])
  }
  const toggle = foldable && (allOpen || folded > 0) && (
    <Button type='link' size='small' onClick={toggleAll}>
      {allOpen ? t('orderJourney.foldDays') : t('orderJourney.openAll', { count: folded })}
    </Button>
  )

  return (
    <Card
      size='small'
      title={<HintLabel label={t('orderJourney.stepsTitle', { days: windowDays })} hint={t('orderJourney.stepsHint')} />}
      extra={toggle}
    >
      {journeyWindow.settled === false && (
        <Alert type='info' showIcon message={t('orderJourney.notSettled')} style={{ marginBottom: 8 }} />
      )}
      {journeyWindow.complete === false && (
        <div style={{ marginBottom: 8 }}>
          <Muted>{t('orderJourney.incomplete', { n: steps.length })}</Muted>
        </div>
      )}
      {steps.length === 0 ? (
        <EmptySteps
          days={windowDays}
          previousActivityAt={journey.previous_activity_at}
          fromLineChat={journey.order?.sales_channel === 'line'}
          lang={lang}
        />
      ) : (
        days.map((day) => (
          <DayBlock
            key={day.dayKey}
            day={day}
            journey={journey}
            lang={lang}
            orderIds={orderIds}
            credited={credited}
            onOpen={() => setOpenedDays([...openedDays, day.dayKey])}
          />
        ))
      )}
      {(steps.length > 0 || after) && <OrderRow journey={journey} />}
      {after && <AfterOrderPart journey={journey} lang={lang} orderIds={orderIds} />}
    </Card>
  )
}

JourneySteps.propTypes = {
  journey: PropTypes.object.isRequired,
  orderIds: PropTypes.instanceOf(Set).isRequired,
  lang: PropTypes.string.isRequired
}

export { JourneySteps }
